import React from 'react';
import Icon from '@ant-design/icons';
import { CloseSvg } from '../../../../assets/icons';
import { BrandSelectorHeaderProps } from '../../types';

const CloseIcon = (props: any) => <Icon component={CloseSvg} {...props} />;

const BrandSelectorHeader: React.FC<BrandSelectorHeaderProps> = ({
  state,
  selectedBrandNames,
  brandOptions,
  fieldKey,
  updateTreeSelectState
}) => {
  return (
    <div className="p-6 border-b border-gray-100">
      {/* Name Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-black/85 mb-2">
          Custom Brand Name
          <span className="text-red-500 ml-1">*</span>
          {state.hasError && (
            <span className="text-red-500 text-xs ml-2">Required field</span>
          )}
        </label>
        <input
          type="text"
          className={`w-full px-4 py-2 border rounded-full text-sm font-['Inter'] focus:outline-none focus:ring-2 ${
            state.hasError 
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
          }`}
          placeholder="Enter name here"
          value={state.tempCustomName}
          onChange={(e) => updateTreeSelectState(fieldKey, { 
            tempCustomName: e.target.value,
            hasError: false // 🎯 typing clears the validation error
          })}
        />
      </div>
      
      {/* Selected Brands */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-black/85 mb-2">
          <p className="h-5.5 text-sm font-medium text-black/85">
            Selected Brands ({selectedBrandNames.length})
            <span className="text-red-500 ml-1">*</span>
          </p>
          <p className="h-4 text-[0.625rem] text-black/35 italic">At least select one core brand</p>
        </label>
        <div className="min-h-[60px] max-h-[100px] overflow-y-auto p-2 pr-4 rounded-3xl border border-[#00000020]">
          {selectedBrandNames.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selectedBrandNames.map((name, index) => (
                <div
                  key={index}
                  className="px-2 py-0.5 bg-slate-50 rounded-[999px] outline-1 -outline-offset-1 outline-black/5 inline-flex justify-center items-center gap-1"
                >
                  <div className="justify-start text-[#0E49B5] text-xs font-normal leading-5">
                    {name}
                  </div>
                  <div
                    className="size-3 relative overflow-hidden cursor-pointer flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Find the value corresponding to this name and remove it
                      let targetValue: string | null = null;
                      
                      const findValue = (nodes: any[]): string | null => {
                        for (const node of nodes) {
                          if (node.title === name) return node.value;
                          if (node.children) {
                            const childValue = findValue(node.children);
                            if (childValue) return childValue;
                          }
                        }
                        return null;
                      };
                      targetValue = findValue(brandOptions);
                      
                      if (targetValue) {
                        const newSelected = state.tempSelectedBrands.filter((val: string) => val !== targetValue);
                        updateTreeSelectState(fieldKey, { tempSelectedBrands: newSelected });
                      }
                    }}
                  >
                    <CloseIcon style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.4)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-1 text-gray-400 text-sm">Select core brands</div>
          )}
        </div>
      </div>
      
      {/* Search Box */}
      <div className="px-4 py-2 rounded-full border border-gray-300 flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          className="flex-1 text-sm font-['Inter'] text-gray-700 placeholder-gray-400 bg-transparent border-none outline-none"
          placeholder="Search"
          value={state.searchValue}
          onChange={(e) => updateTreeSelectState(fieldKey, { searchValue: e.target.value })}
        />
      </div>
    </div>
  );
};

export default BrandSelectorHeader;

